import { type FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MdCalendarToday, MdArrowBack, MdViewWeek, MdViewModule, MdTimeline } from 'react-icons/md';
import Calendar from 'react-calendar';
import { toast } from 'react-toastify';
import { format, isSameDay, parseISO } from 'date-fns';
import type { Task } from '../types';
import { getTasks } from '../services/api';
import { useTheme } from '../hooks/useTheme';
import { CalendarSkeleton } from '../components/Skeleton';
import TaskCard from '../components/TaskCard';
import TaskTimeline from '../components/TaskTimeline';
import HeatMapCalendar from '../components/HeatMapCalendar';
import 'react-calendar/dist/Calendar.css';

type CalendarView = 'month' | 'timeline' | 'heatmap';

const CalendarPage: FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [tasksForSelectedDate, setTasksForSelectedDate] = useState<Task[]>([]);
  const [calendarView, setCalendarView] = useState<CalendarView>('month');

  useEffect(() => {
    loadTasks();
  }, []);

  useEffect(() => {
    // Filter tasks for the selected date
    const filtered = tasks.filter((task) => {
      if (!task.due_date) return false;
      return isSameDay(parseISO(task.due_date), selectedDate);
    });
    setTasksForSelectedDate(filtered);
  }, [selectedDate, tasks]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const data = await getTasks({ is_archived: false });
      setTasks(data);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (value: unknown) => {
    if (value instanceof Date) {
      setSelectedDate(value);
    }
  };

  const getTileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return null;

    const tasksOnDate = tasks.filter((task) => {
      if (!task.due_date) return false;
      return isSameDay(parseISO(task.due_date), date);
    });

    if (tasksOnDate.length === 0) return null;

    return (
      <div className="flex justify-center gap-1 mt-1">
        {tasksOnDate.slice(0, 3).map((task, index) => {
          const color =
            task.priority === 'high'
              ? 'bg-neon-pink'
              : task.priority === 'medium'
              ? 'bg-theme'
              : 'bg-neon-green';
          return (
            <div
              key={index}
              className={`w-1.5 h-1.5 rounded-full ${color}`}
              title={task.title}
            />
          );
        })}
        {tasksOnDate.length > 3 && (
          <span className="text-[8px] dark:text-gray-400 text-gray-600">
            +{tasksOnDate.length - 3}
          </span>
        )}
      </div>
    );
  };

  if (loading) {
    return <CalendarSkeleton />;
  }

  // Get accent color with opacity for gradient
  const getAccentColor = () => {
    const colors: Record<string, string> = {
      blue: '0, 255, 255',
      green: '0, 255, 0',
      purple: '139, 0, 255',
      pink: '255, 0, 255',
      orange: '255, 107, 0',
      yellow: '255, 255, 0'
    };
    return colors[document.documentElement.style.getPropertyValue('--color-theme-name')?.trim() || 'blue'];
  };

  return (
    <div 
      className="min-h-screen py-8 px-4"
      style={{
        background: theme === 'dark'
          ? `linear-gradient(135deg, rgba(17, 24, 39, 1) 0%, rgba(${getAccentColor()}, 0.03) 100%)`
          : `linear-gradient(135deg, rgba(240, 244, 248, 1) 0%, rgba(${getAccentColor()}, 0.05) 100%)`
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <MdCalendarToday className="w-8 h-8 dark:text-theme text-theme" />
              <h1 className="text-3xl font-bold" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
                Calendar View
              </h1>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard')}
              className="
              flex items-center gap-2
              px-4 py-2
              rounded-lg
              border-2
              font-semibold
              hover:shadow-theme
              focus:outline-none
              focus:ring-2
              focus:ring-theme
              transition-all
              "
              style={{
              backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
              borderColor: 'var(--color-theme)',
              color: 'var(--color-theme)'
              }}
            >
              <MdArrowBack className="w-5 h-5" />
              Back to Dashboard
            </motion.button>
          </div>

          {/* View Switcher Tabs */}
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => setCalendarView('month')}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all
                ${calendarView === 'month' ? 'shadow-lg' : ''}
              `}
              style={{
                backgroundColor: calendarView === 'month' 
                  ? 'var(--color-theme)' 
                  : (theme === 'dark' ? '#374151' : '#e5e7eb'),
                color: calendarView === 'month' 
                  ? '#ffffff' 
                  : (theme === 'dark' ? '#9ca3af' : '#6b7280')
              }}
            >
              <MdViewModule className="w-5 h-5" />
              Month View
            </button>
            <button
              onClick={() => setCalendarView('timeline')}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all
                ${calendarView === 'timeline' ? 'shadow-lg' : ''}
              `}
              style={{
                backgroundColor: calendarView === 'timeline' 
                  ? 'var(--color-theme)' 
                  : (theme === 'dark' ? '#374151' : '#e5e7eb'),
                color: calendarView === 'timeline' 
                  ? '#ffffff' 
                  : (theme === 'dark' ? '#9ca3af' : '#6b7280')
              }}
            >
              <MdTimeline className="w-5 h-5" />
              Timeline
            </button>
            <button
              onClick={() => setCalendarView('heatmap')}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all
                ${calendarView === 'heatmap' ? 'shadow-lg' : ''}
              `}
              style={{
                backgroundColor: calendarView === 'heatmap' 
                  ? 'var(--color-theme)' 
                  : (theme === 'dark' ? '#374151' : '#e5e7eb'),
                color: calendarView === 'heatmap' 
                  ? '#ffffff' 
                  : (theme === 'dark' ? '#9ca3af' : '#6b7280')
              }}
            >
              <MdViewWeek className="w-5 h-5" />
              Heat Map
            </button>
          </div>

          <p style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
            {calendarView === 'month' && 'View tasks organized by due date'}
            {calendarView === 'timeline' && 'See your tasks in a weekly timeline'}
            {calendarView === 'heatmap' && 'Visualize task density over time'}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calendar Views */}
          {calendarView === 'month' && (
            <>
              {/* Month Calendar */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="
                  p-6
                  rounded-lg
                  shadow-lg
                  border-2
                "
                style={{
                  backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
                  borderColor: 'var(--color-theme)'
                }}
              >
                <Calendar
                  onChange={handleDateChange}
                  value={selectedDate}
                  tileContent={getTileContent}
                  className="
                    w-full
                    dark:bg-dark-card
                    bg-white
                    rounded-lg
                    border-none
                    [&_.react-calendar__tile]:dark:text-white
                    [&_.react-calendar__tile]:text-gray-900
                    [&_.react-calendar__tile--active]:dark:bg-theme-30
                    [&_.react-calendar__tile--active]:bg-blue-200
                    [&_.react-calendar__tile--now]:dark:bg-neon-purple/20
                    [&_.react-calendar__tile--now]:bg-purple-100
                    [&_.react-calendar__tile:hover]:dark:bg-dark-hover
                    [&_.react-calendar__tile:hover]:bg-gray-100
                    [&_.react-calendar__navigation\_\_label]:dark:text-white
                    [&_.react-calendar__navigation\_\_label]:text-gray-900
                    [&_.react-calendar__navigation\_\_arrow]:dark:text-theme
                    [&_.react-calendar__navigation\_\_arrow]:text-theme
                    [&_.react-calendar__month-view\_\_weekdays]:dark:text-gray-400
                    [&_.react-calendar__month-view\_\_weekdays]:text-gray-600
                  "
                />
                
                {/* Legend */}
                <div 
                  className="mt-6 pt-6 border-t"
                  style={{ borderColor: 'var(--color-theme)' }}
                >
                  <h3 className="text-sm font-semibold mb-3" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
                    Priority Legend
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-neon-pink" />
                      <span className="text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
                        High Priority
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-theme" />
                      <span className="text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
                        Medium Priority
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-neon-green" />
                      <span className="text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
                        Low Priority
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Tasks for Selected Date */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="
                  p-6
                  rounded-lg
                  shadow-lg
                  border-2
                "
                style={{
                  backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
                  borderColor: 'var(--color-theme)'
                }}
              >
                <h2 className="text-xl font-bold mb-4" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
                  Tasks for {format(selectedDate, 'MMMM d, yyyy')}
                </h2>

                {tasksForSelectedDate.length === 0 ? (
                  <div 
                    className="
                      text-center
                      py-12
                      border-2 border-dashed
                      rounded-lg
                    "
                    style={{ borderColor: 'var(--color-theme)' }}
                  >
                    <MdCalendarToday 
                      className="w-12 h-12 mx-auto mb-3"
                      style={{ color: 'var(--color-theme)' }}
                    />
                    <p style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
                      No tasks due on this date
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                    {tasksForSelectedDate.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onEdit={() => navigate('/dashboard')}
                        onDelete={() => {}}
                        onArchive={() => {}}
                      />
                    ))}
                  </div>
                )}

                {/* Summary */}
                {tasksForSelectedDate.length > 0 && (
                  <div 
                    className="mt-6 pt-6 border-t"
                    style={{ borderColor: 'var(--color-theme)' }}
                  >
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold dark:text-neon-pink text-pink-600">
                          {tasksForSelectedDate.filter((t) => t.priority === 'high').length}
                        </p>
                        <p className="text-xs" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>High</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold dark:text-neon-blue text-blue-600">
                          {tasksForSelectedDate.filter((t) => t.priority === 'medium').length}
                        </p>
                        <p className="text-xs" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>Medium</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold dark:text-neon-green text-green-600">
                          {tasksForSelectedDate.filter((t) => t.priority === 'low').length}
                        </p>
                        <p className="text-xs" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>Low</p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </>
          )}

          {/* Timeline View */}
          {calendarView === 'timeline' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="lg:col-span-2"
            >
              <div 
                className="p-6 rounded-lg shadow-lg border-2"
                style={{
                  backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
                  borderColor: 'var(--color-theme)'
                }}
              >
                <TaskTimeline 
                  tasks={tasks} 
                  onTaskClick={(task) => {
                    setSelectedDate(new Date(task.due_date!));
                    setCalendarView('month');
                  }}
                />
              </div>
            </motion.div>
          )}

          {/* Heat Map View */}
          {calendarView === 'heatmap' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="lg:col-span-2"
            >
              <div 
                className="p-6 rounded-lg shadow-lg border-2"
                style={{
                  backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
                  borderColor: 'var(--color-theme)'
                }}
              >
                <HeatMapCalendar 
                  tasks={tasks}
                  onDayClick={(date, dayTasks) => {
                    if (dayTasks.length > 0) {
                      setSelectedDate(date);
                      setCalendarView('month');
                    }
                  }}
                />
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
